// Establish a repo's base (#1728) — `POST /v1/repos/:owner/:repo/setup`, the
// brownfield sibling of `lib/project-create.ts`. A box clones the repo, audits
// what is there and lays the day-one base — stack detect, `bin/{setup,dev,check}`,
// `.mcp.json`, a project brain — never clobbering a maintainer's own files.
//
// A HUMAN HAS TO ASK, PER REPO, and that is the whole reason this door exists.
// `apps/api/src/deps/project-setup.ts` refuses to fan setup tasks out on enroll:
// a session spends the account's own inference budget, and the control plane
// does not know which repos are on which box's disk. So without a control the
// capability has no delivery mechanism for a person at all.
//
// NO BODY. The repo full name IS the whole request. The brief's posture belongs
// to the port and `apps/runner` setup `base.ts` owns the mechanics — a form that
// could script the steps would be the hardcoded state machine this repo bans.
//
// A 202 IS NOT A COMPLETED SETUP. The route answers a TASK id and nothing else;
// a box mints the run id when it CLAIMS the task, so there is no run to tail or
// link to yet. Dispatched-then-claimed is the shape the result renders — the
// `plan-start.ts` rule, and the queue board is where it points.
//
// THE REFUSALS ARE THE PORT'S. Non-ok is a 422 carrying its sentence verbatim —
// deliberately not a 404 and not a 409, because the port already tells "not this
// account's repo" apart from "not enabled" and renders the actionable line
// itself. The one 404 that CAN reach this client is the route's own absence
// (consumer-or-absent: what a deployment may be missing here is a dispatcher,
// not a credential), which is a different sentence and gets its own key.

import { ApiError } from './api';
import { type ApiCall, sendApiCall } from './api-call';
import { SESSION_TOKEN } from './session';

/** What `POST …/setup` did, as this client can honestly report it. */
export type ProjectSetupOutcome =
  /** 202: the setup task is dispatched. Its `run_id` is null until a box claims it. */
  | { readonly kind: 'started'; readonly task_id: string }
  /** A 2xx that named no task — the `readStartOutcome` rule. */
  | { readonly kind: 'unclear' }
  /** Any non-2xx: `status` frames the copy, `detail` is the server's own sentence. */
  | { readonly kind: 'refused'; readonly status: number; readonly detail: string | null };

/**
 * `owner/repo` → the route path, encoded per segment. Same rule as
 * `repoApiBase` (`lib/repo-detail.ts`): the API routes `/:owner/:repo` as two
 * percent-encoded params, so a repo whose name needs escaping addresses the same
 * row from either surface.
 */
function setupPath(fullName: string): string {
  const [owner = '', ...rest] = fullName.split('/');
  return `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(rest.join('/'))}/setup`;
}

/**
 * The dispatch as ONE value — fired by {@link startProjectSetup}, printed by
 * `<CopyAsCall/>`. The MCP half is the `project` tool's `setup` action, whose
 * own argument is the `owner/repo` full name (`projectRepoField`), not the
 * opaque id: the two doors address the repo the same way.
 */
export function projectSetupCall(fullName: string): ApiCall {
  return {
    capability: 'project.setup',
    method: 'POST',
    path: setupPath(fullName),
    mcp: { kind: 'verb', verb: 'project.setup', arguments: { repo: fullName } },
  };
}

/**
 * Dispatch the setup session. Never throws: a refusal IS an outcome (a 401 has
 * already latched the session-expiry gate in `apiFetch`), and a dead network has
 * no status, so it answers 0 and reads as the generic line.
 */
export async function startProjectSetup(
  fullName: string,
  token: string | null = SESSION_TOKEN,
): Promise<ProjectSetupOutcome> {
  try {
    const body = await sendApiCall<unknown>(projectSetupCall(fullName), token);
    const taskId =
      typeof body === 'object' && body !== null
        ? (body as Record<string, unknown>)['task_id']
        : undefined;
    return typeof taskId === 'string' && taskId !== ''
      ? { kind: 'started', task_id: taskId }
      : { kind: 'unclear' };
  } catch (err) {
    if (err instanceof ApiError) {
      return { kind: 'refused', status: err.status, detail: err.detail ?? null };
    }
    return { kind: 'refused', status: 0, detail: null };
  }
}

/**
 * Copy key framing a refusal. A 403 is the scope (`write:tasks`), a 404 is the
 * DEPLOYMENT — the route is registered only where a dispatcher backs it, and a
 * repo this account cannot set up answers 422, not 404 — and a 422 is the port
 * declining a well-formed ask. Everything else is generic, and the panel prints
 * the API's own `detail` under whichever line it picks.
 */
const REFUSAL_KEYS: Record<number, string> = {
  403: 'repo_detail.setup_refused_scope',
  404: 'repo_detail.setup_refused_absent',
  422: 'repo_detail.setup_refused_rejected',
};

export const setupRefusalKey = (status: number): string =>
  REFUSAL_KEYS[status] ?? 'repo_detail.setup_refused_other';
